/**
 * useFocusTrap — shared WAI-ARIA focus-trap for modal dialogs.
 *
 * T-Cron-2026-10-22 08:00 早间 (round-1-of-day) — a11y #10.
 *
 * 背景:根目录老仓库有 components/useFocusTrap.js(T-Cron-2026-10-19 提取),
 * 覆盖 5 个弹层(ShortcutsHelp / PostDetail / SearchOverlay / QuickActions /
 * MobileFilterDrawer)。但线上跑的 Vite 版是另一棵树,没有这个 hook ——
 * PostDetail 的抽屉是 role="dialog" 却缺 aria-modal,MobileFilterDrawer
 * 连 role="dialog" 都没有。后果:键盘用户打开弹层后可以 Tab 到弹层**背后**
 * 的看板卡片上,焦点完全跑丢(既看不见当前上下文,也不知道自己在哪)。
 *
 * 行为:
 *   - Tab 在最后一个可聚焦元素上 → 回到第一个;
 *   - Shift+Tab 在第一个可聚焦元素上 → 跳到最后一个;
 *   - 焦点逃出面板(比如用户点了遮罩再 Tab 回来)→ 拉回第一个可聚焦元素;
 *   - 打开时(autoFocus)把焦点移进面板,关闭时还给触发元素。
 *
 * 可聚焦选择器遵循 WAI-ARIA Authoring Practices,并过滤掉不可见元素
 * (display:none / visibility:hidden / aria-hidden="true" / 零尺寸),
 * 避免焦点落到看不见的控件上。
 *
 * 定义成普通函数挂在 window 上,和这个代码库其他共享 helper
 * (CoverDownload / ContentService / useContentStore)保持同一套约定 ——
 * 组件里既可以 import,也可以直接用 window.useFocusTrap。
 */
import React from 'react';

(function () {
  if (typeof window === 'undefined') return;

  const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');

  function getFocusable(root) {
    if (!root) return [];
    return Array.from(root.querySelectorAll(FOCUSABLE_SELECTOR)).filter((el) => {
      if (el.getAttribute('aria-hidden') === 'true') return false;
      const rect = el.getBoundingClientRect();
      return rect.width > 0 && rect.height > 0;
    });
  }

  window.useFocusTrap = function useFocusTrap(panelRef, options) {
    const opts = options || {};
    const autoFocus = !!opts.autoFocus;
    // enabled 默认 true。弹层是条件挂载时传 enabled: open,
    // 这样 open=false 时 effect 直接 no-op,也不会挂上 keydown 监听。
    const enabled = opts.enabled === undefined ? true : !!opts.enabled;

    // 🔴 T-Cron-2026-11-03 08:00 早间 (T139) — 修「嵌套模态的焦点归还竞态」。
    //
    // 【缺陷 —— 本轮已用运行时探针实测复现】
    //   PostDetail 里有两个**嵌套**模态:
    //     抽屉 drawerRef → useFocusTrap({ enabled: open && !sheet, autoFocus: true })
    //     面板 sheetRef  → useFocusTrap({ enabled: !!sheet,       autoFocus: true })
    //   面板(保存封面)打开时,抽屉的 trap 被**禁用** ⇒ 抽屉的 effect 走 cleanup
    //   ⇒ cleanup 把焦点还给抽屉背后的卡片(焦点跑到模态背后)。
    //   面板关闭时:面板的 cleanup 把焦点还给触发元素(「保存 A 版封面」),
    //   **紧接着**抽屉的 trap 重新启用 ⇒ 抽屉的 effect **重跑**
    //   ⇒ autoFocus 把焦点拉到抽屉里的**第一个**可聚焦元素(「关闭」按钮)。
    //   ⇒ 实测:打开面板 → 关闭面板 ⇒ 焦点落在「关闭」而不是「保存 A 版封面」
    //     ⇒ **用户被拽回抽屉顶部,丢失了原来的位置**
    //     (触屏上尤其明显 —— 保存面板只有触屏才会打开)。
    //
    // 【三个根因,三个修法】
    //   ① **重新启用时不该抢焦点** ——
    //     autoFocus 的语义是「弹层**刚打开**时把焦点拉进来」,
    //     不是「每次 effect 重跑都拉」。
    //     ⇒ 只有当焦点**不在**面板内时才 autoFocus。
    //     面板关闭后焦点已被面板的 cleanup 还给触发元素(在抽屉内)
    //     ⇒ 抽屉重新启用时发现「焦点已在面板内」⇒ 不抢 ⇒ 位置保住。
    //
    //   ② **临时禁用(enabled: true → false)不该把焦点扔回触发元素** ——
    //     抽屉的 trap 被禁用只是因为「上面盖了个面板」,抽屉**并没有关闭**。
    //     此时把焦点还给抽屉背后的卡片 = 焦点跑到模态背后 ⇒ 焦点丢失。
    //
    //   ③ **「临时禁用」与「真正关闭」必须能被区分** ——
    //     ⚠️ 靠启发式区分**全部失败过**(T139 实测踩了 3 次):
    //        · 「enabled 从 true 变 false」?❌ 保存面板真正关闭时 enabled
    //          也变 false ⇒ 把「真正关闭」误判成「临时禁用」
    //          ⇒ 面板关闭后焦点不归还,停在 body。
    //        · 「面板还在不在 DOM 里」?❌ 本项目**所有**弹层都是
    //          「常驻挂载 + CSS class 切换」(.drawer / .mobile-filter-drawer
    //          都是 transform 移出视口,不是 display:none / 条件渲染)
    //          ⇒ document.contains(panel) 永远为 true
    //          ⇒ 把「真正关闭」误判成「临时禁用」
    //          ⇒ 筛选抽屉关闭后焦点不归还(实测落在「关闭筛选」)。
    //     ⇒ 唯一可靠的判据是**调用方自己知道**:
    //       弹层「暂时让位给上层面板」时传 `restoreFocus: false`,
    //       弹层「真的关闭」时传 `restoreFocus: true`(或省略,默认 true)。
    //
    // 【restoreFocus 选项】
    //   · 省略 / true  → effect cleanup 时把焦点还给打开前的元素(默认行为)
    //   · false        → cleanup 时**不动**焦点
    //     用在「本弹层只是临时让位给上层面板」的场景。
    //   ⚠️ 它是**渲染期**的值(不是 cleanup 期判据)——
    //      所以调用方要保证:让位时传 false,真正关闭时传 true。
    //      PostDetail 的抽屉正是这个形状:
    //        enabled: open && !sheet
    //        restoreFocus: !sheet      ← sheet 开着 = 临时让位 = 不还焦点
    //                                      sheet 关了 = 抽屉要关了 = 还焦点
    const restoreFocus = opts.restoreFocus === undefined ? true : !!opts.restoreFocus;
    //
    // 【⚠️ cleanup 读的是「最新」的 restoreFocus,不是闭包里那份】
    //   effect 的依赖数组含 restoreFocus ⇒ 它的值一变,React 会先跑
    //   **旧 effect 的 cleanup** ⇒ 而旧 cleanup 闭包里捕获的是**旧值**。
    //   ⇒ PostDetail 抽屉的形状:
    //       面板打开:enabled true→false,restoreFocus true→false
    //       ⇒ 旧 cleanup 拿到 restoreFocus=true ⇒ 会把焦点还给卡片(坏!)
    //   ⇒ 所以必须在**渲染期**把当前值写进 ref,cleanup 时读 ref。
    const restoreFocusRef = React.useRef(restoreFocus);
    restoreFocusRef.current = restoreFocus;   // 渲染期同步

    React.useEffect(() => {
      if (!enabled) return;
      const panel = panelRef.current;
      if (!panel) return;

      // 记住打开前的焦点,关闭时还回去(键盘用户不会"丢失位置")
      const previouslyFocused = document.activeElement;

      const focusables = () => getFocusable(panel);

      if (autoFocus) {
        // 优先聚焦第一个可聚焦元素;面板里一个都没有时,退而聚焦面板本身
        // (面板需要有 tabIndex=-1 才能接住焦点)。
        const list = focusables();
        const target = list[0] || panel;
        // 等一帧,确保面板已渲染完成
        const t = setTimeout(() => {
          // 🔴 T139:焦点**已经在面板内**时不要抢 ——
          //   说明这是「重新启用」(嵌套模态里下层弹层被临时禁用又启用),
          //   不是「刚打开」。抢焦点会把用户从刚归还的触发元素上拽到面板顶部
          //   ⇒ 位置丢失。
          const active = document.activeElement;
          if (active && panel.contains(active)) return;
          try { target.focus({ preventScroll: true }); } catch (_) {}
        }, 0);
        var timer = t;
      }

      const handleKeyDown = (e) => {
        if (e.key !== 'Tab') return;
        const list = focusables();
        if (list.length === 0) {
          // 面板内无可聚焦元素:把焦点钉在面板上,不让用户 Tab 出去
          e.preventDefault();
          try { panel.focus({ preventScroll: true }); } catch (_) {}
          return;
        }
        const first = list[0];
        const last = list[list.length - 1];
        const active = document.activeElement;

        if (e.shiftKey) {
          // Shift+Tab 在第一个之前 → 跳到最后一个
          if (active === first || !panel.contains(active)) {
            e.preventDefault();
            last.focus();
          }
        } else {
          // Tab 在最后一个之后 → 跳回第一个
          if (active === last || !panel.contains(active)) {
            e.preventDefault();
            first.focus();
          }
        }
      };

      document.addEventListener('keydown', handleKeyDown, true);

      return () => {
        document.removeEventListener('keydown', handleKeyDown, true);
        if (timer) clearTimeout(timer);
        // 焦点还回触发元素(只在它还在文档里时)。
        // 🔴 T139:restoreFocus=false 时**不动**焦点 ——
        //   场景是「本弹层只是临时让位给上层面板」(嵌套模态),
        //   焦点应该留在上层面板里,不该被扔回本弹层背后的卡片。
        if (!restoreFocusRef.current) return;
        if (previouslyFocused && typeof previouslyFocused.focus === 'function'
            && document.contains(previouslyFocused)) {
          try { previouslyFocused.focus({ preventScroll: true }); } catch (_) {}
        }
      };
    }, [enabled, autoFocus, restoreFocus, panelRef]);
  };
})();

export default window.useFocusTrap;
